import React, { useState } from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import FilePicker from "components/FilePicker";
import { useSnackbar } from "hooks/useSnackbar";
import { uploadContentTranslations } from "services/uploadService";
import sessionHelper from "services/sessionHelper";
import * as XLSX from "xlsx";

export interface TranslationField {
  /** Display label shown as the sheet name, e.g. "Title", "Body" */
  label: string;
  /** The backend field key, e.g. "titleTrans", "bodyTrans", "nameTrans" */
  fieldName: string;
  /** Optional custom dummy text for the template. When set, this replaces the default per-language placeholder. */
  dummyText?: string;
}

interface BulkTranslationUploadProps {
  assetType: string;
  assetId: string;
  /** Translatable fields — one sheet per field in the template */
  fields: TranslationField[];
  disabled?: boolean;
  /** Called after a successful upload */
  onSuccess?: () => void;
}

const useStyles = makeStyles((theme) => ({
  card: {
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#ffffff",
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    "& svg": {
      fontSize: "1.5rem",
      color: theme.palette.primary.main,
    },
  },
  infoBox: {
    padding: "12px 16px",
    borderRadius: "8px",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    "& svg": {
      color: "#1976d2",
    },
  },
  uploadSection: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 16,
    alignItems: "flex-end",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
}));

const BulkTranslationUpload = ({
  assetType,
  assetId,
  fields,
  disabled = false,
  onSuccess,
}: BulkTranslationUploadProps) => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const onSelectFile = (selectedFile: File | null) => {
    setFile(selectedFile);
  };

  const onUpload = async () => {
    if (!file) return;
    try {
      setLoading(true);
      const response = await uploadContentTranslations(
        file,
        assetType,
        assetId,
      );
      setLoading(false);
      setFile(null);
      if (!response.status) throw new Error(response?.message);
      showSnackbar(response.message || "Translations uploaded successfully");
      onSuccess?.();
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Get all language codes from session
    const languages: { isoCode: string; name: string }[] =
      sessionHelper.languages || [];

    for (const field of fields) {
      const rows = languages.map((lang) => ({
        Locale: lang.isoCode,
        Text: field.dummyText || `Enter "${lang.name}" text here`,
        _fieldName: field.fieldName,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths for readability
      ws["!cols"] = [{ wch: 10 }, { wch: 45 }, { wch: 20 }];

      XLSX.utils.book_append_sheet(wb, ws, field.label);
    }

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${assetType.toLowerCase()}_translation_template.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fieldLabels = fields.map((f) => f.label).join(", ");

  return (
    <Card className={styles.card}>
      <Box className={styles.sectionHeader}>
        <TranslateIcon />
        <Typography variant="h6" fontWeight={600}>
          Bulk Translation Upload
        </Typography>
      </Box>
      <Box className={styles.infoBox}>
        <InfoOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          Upload an Excel file (.xlsx) to update translations for{" "}
          <strong>{fieldLabels}</strong>. Download the template first — it
          contains one sheet per field with all supported languages
          pre-populated.
        </Typography>
      </Box>
      <Box className={styles.uploadSection}>
        <Box flex={1}>
          <FilePicker
            label="Select Translation File"
            name="bulk-translation"
            fileType=".csv, .xlsx, .xls"
            value={file}
            showPreview={false}
            onFileSelect={onSelectFile}
          />
        </Box>
        <Box className={styles.actions}>
          <Button
            variant="outlined"
            onClick={onDownloadTemplate}
            startIcon={<DownloadIcon />}
            size="large"
          >
            Download Template
          </Button>
          <Button
            disabled={!file || disabled || loading}
            variant="contained"
            onClick={onUpload}
            startIcon={<UploadFileIcon />}
            size="large"
          >
            {loading ? "Uploading..." : "Upload File"}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default BulkTranslationUpload;
